import type { ris } from "@progressive-development/politik-model";

/**
 * Die Regeln der Kennzahlen — reine Funktionen, weil sie Aussagen sind.
 *
 * Sie stehen neben der Seite und nicht in der Komponente: Was hier entschieden
 * wird, ist prüfbar und wird geprüft.
 */

/** Der Anteil in ganzen Prozent — ohne Bezugsgröße keine Aussage. */
export function anteil(anzahl: number, gesamt: number): number | undefined {
  if (!gesamt || gesamt < 0) return undefined;
  return Math.round((anzahl / gesamt) * 100);
}

/** Wie oft eine Urheber-Art vorkommt; fehlt sie, ist es keine 0, sondern nichts. */
export function urheberAnzahl(
  urheber: ris.GremiumKennzahlen["urheber"],
  art: string,
): number | undefined {
  return urheber?.find((u) => u.art === art)?.anzahl;
}

/**
 * Darf die Aufschlüsselung nach Fraktionen gezeigt werden?
 *
 * NUR wenn die Quelle die Fraktion auch überwiegend nennt. Der Bestand ist an
 * dieser Stelle zweigeteilt, und zwar drastisch: In sechs Bezirksvertretungen
 * ist die Fraktion bei 99 bis 100 Prozent der Vorlagen benannt, in dreien bei
 * 0 bis 2 Prozent (Lindenthal 0 von 79, Mülheim 2 von 82, Porz 1 von 106).
 *
 * Das ist kein Unterschied im Verhalten der Gremien, sondern in der
 * Beschriftungspraxis des jeweiligen Bezirksamts. Eine Liste, die auf einem
 * Prozent der Fälle beruht, sagt das GEGENTEIL der Wahrheit: Sie zeigte in
 * Porz eine einzige Fraktion mit einem einzigen Antrag, während 105 weitere
 * Anträge von Fraktionen ungenannt danebenliegen.
 *
 * Die Schwelle liegt bei 90 Prozent. Weil der Bestand zweigeteilt ist, träfe
 * jeder Wert zwischen 10 und 90 dieselbe Entscheidung — hoch angesetzt ist sie
 * deshalb, weil der gefährliche Fall der halb zugeordnete wäre: eine Liste,
 * die vollständig AUSSIEHT und es nicht ist.
 */
export const SCHWELLE_ZUORDNUNG = 0.9;

export function fraktionenZeigen(urheber: ris.GremiumKennzahlen["urheber"]): boolean {
  const benannt = urheberAnzahl(urheber, "fraktion") ?? 0;
  const ungenannt = urheberAnzahl(urheber, "fraktion-ungenannt") ?? 0;
  const gesamt = benannt + ungenannt;
  if (gesamt === 0) return false;
  return benannt / gesamt >= SCHWELLE_ZUORDNUNG;
}

/**
 * Die Kennzahlen EINES Gremiums aus dem gemeinsamen Dokument.
 *
 * Über die Kennung des Gremiums, nie über den Namen. Fehlt der Eintrag, fehlt
 * er — geraten wird nicht.
 */
export function kennzahlenVon(
  dokument: ris.GremienKennzahlenDokument | undefined,
  gremiumId: string | undefined,
): ris.GremiumKennzahlen | undefined {
  if (!dokument || !gremiumId) return undefined;
  return dokument.gremien?.find((g) => g.gremium === gremiumId);
}
